import {
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Progress,
  Text,
  Tooltip,
  useColorModeValue as mode,
  useDisclosure,
} from '@chakra-ui/react';
import { useToast } from '@chakra-ui/react';
import {
  Building2,
  Eye,
  Infinity as InfinityIcon,
  Pencil,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import AddCompanyModal from './AddCompanyModal';
import DeleteCompanyModal from './DeleteCompanyModal';
import EditCompanyModal from './EditCompanyModal';
import AmatriumTableV2 from '../../components/AmatriumTableV2';
import { PageContainer, PageHeader } from '../../components/ds';
import Paginate from '../../components/Paginate';
import {
  useCreateOrganization,
  useDeleteOrganization,
  useOrganizations,
  useUpdateOrganizationLimitations,
} from '../../queries/useOrganization';
import {
  CreateOrganizationPayload,
  Organization,
  OrganizationLimitation,
} from '../../services/organization';

const ManagementSystem = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const history = useHistory();
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  // Utility function to format numbers
  const formatNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';

    // If it's a decimal number with more than 2 decimal places, round to 2
    if (value % 1 !== 0) {
      const rounded = Math.round(value * 100) / 100;
      return rounded.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    }

    // If it's a whole number, add thousand separators
    return value.toLocaleString('en-US');
  };

  const {
    isOpen: isAddCompanyModalOpen,
    onOpen: onOpenAddCompanyModal,
    onClose: onCloseAddCompanyModal,
  } = useDisclosure();

  const {
    isOpen: isDeleteCompanyModalOpen,
    onOpen: onOpenDeleteCompanyModal,
    onClose: onCloseDeleteCompanyModal,
  } = useDisclosure();

  const {
    isOpen: isEditCompanyModalOpen,
    onOpen: onOpenEditCompanyModal,
    onClose: onCloseEditCompanyModal,
  } = useDisclosure();
  const [companyToDelete, setCompanyToDelete] = useState<Organization | null>(null);
  const [companyToEdit, setCompanyToEdit] = useState<Organization | null>(null);
  const [pageSize] = useState(10);

  // Check if any modal is open
  const isAnyModalOpen =
    isAddCompanyModalOpen || isDeleteCompanyModalOpen || isEditCompanyModalOpen;

  // Force close all tooltips when modal closes
  useEffect(() => {
    if (!isAnyModalOpen) {
      // Force close any open tooltips by dispatching a click event
      const tooltips = document.querySelectorAll('[data-tooltip]');
      tooltips.forEach((tooltip) => {
        const event = new MouseEvent('mouseleave', {
          bubbles: true,
          cancelable: true,
        });
        tooltip.dispatchEvent(event);
      });
    }
  }, [isAnyModalOpen]);

  // React Query hooks
  const { data: companyData, isFetching } = useOrganizations(
    {
      page: currentPage,
      size: pageSize,
      name: searchTerm || undefined,
    },
    {
      refetchOnWindowFocus: true,
      staleTime: 0, // Data sẽ được coi là stale ngay lập tức
    } as any
  );

  const createOrganizationMutation = useCreateOrganization();
  const deleteOrganizationMutation = useDeleteOrganization();
  const updateLimitationsMutation = useUpdateOrganizationLimitations();

  // Color mode values
  const colorMainText = mode('color_main_text', 'color_main_text');
  const colorSecondaryText = mode('color_main_text', 'color_main_text');

  // DS quota cell — used/limit labels + Progress variant="ds" (design-guidelines.md §4)
  const QuotaCell = ({
    used,
    limit,
    formatValue = formatNumber,
  }: {
    used: number;
    limit: number;
    formatValue?: (v: number | null | undefined) => string;
  }) => {
    const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
    // Usage health (mockup Organizations): green normal → amber high → red near/over limit.
    const meterColor = pct >= 90 ? 'state.error' : pct >= 75 ? 'state.warning' : 'state.success';
    return (
      <Tooltip label={`${formatValue(used)} / ${formatValue(limit)} · ${pct}%`} placement='top'>
        <Box minW='70px'>
          <Flex justify='space-between' mb='4px' color='text.muted' fontFamily='mono' fontSize='xs'>
            <Text as='span'>{formatValue(used)}</Text>
            <Text as='span'>{formatValue(limit)}</Text>
          </Flex>
          <Progress
            sx={{ '& > div[role="progressbar"]': { bg: meterColor } }}
            h='8px'
            borderRadius='full'
            value={pct}
            variant='ds'
          />
        </Box>
      </Tooltip>
    );
  };

  const UnlimitedTag = () => (
    <Flex align='center' gap={1} color='text.muted' fontSize={{ base: 'xs', md: 'sm' }}>
      <Icon as={InfinityIcon} boxSize='14px' />
      {t('managementSystem.unlimited')}
    </Flex>
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Debounced search function with useRef to persist timeout
  const searchTimeoutRef = React.useRef<NodeJS.Timeout>();

  const debouncedSearch = useCallback((_searchValue: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(0); // Reset to first page when searching
      // React Query sẽ tự động refetch khi searchTerm thay đổi
    }, 500); // 500ms delay
  }, []);

  // Handle search input change
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    setSearchTerm(value);
    setCurrentPage(0); // reset page synchronously (RQ refetches on searchTerm change, staleTime 0)
    debouncedSearch(value);
  };

  const handleSearch = () => {
    setCurrentPage(0); // Reset to first page when searching
    // React Query sẽ tự động refetch khi searchTerm thay đổi
  };

  const handleAddCompany = () => {
    onOpenAddCompanyModal();
  };

  const handleSubmitCompany = (companyData: CreateOrganizationPayload) => {
    createOrganizationMutation.mutate(companyData, {
      onSuccess: (data: any) => {
        if (!data?.success && data?.message) {
          return;
        }
        // Close modal when success
        onCloseAddCompanyModal();
        // Show success message from API - API returns simple string message
        const successMessage = typeof data === 'string' ? data : data?.message;
        toast({
          title: t('managementSystem.toast.success'),
          description: successMessage,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      },
      onError: (error: any) => {
        // Keep modal open when error and show error toast with API message
        console.error('Failed to create organization:', error);
      },
    });
  };

  const handleEditCompany = (company: Organization) => {
    setCompanyToEdit(company);
    onOpenEditCompanyModal();
  };

  const handleViewUsage = (company: Organization) => {
    // Mockup parity: route to the full-PAGE org usage & members view (not a modal).
    history.push(`/management-system/${company.id}`, { company });
  };

  const handleDeleteCompany = (company: Organization) => {
    setCompanyToDelete(company);
    onOpenDeleteCompanyModal();
  };

  const handleConfirmDelete = (organization: Organization) => {
    deleteOrganizationMutation.mutate(organization.id, {
      onSuccess: (data: any) => {
        if (!data?.success && data?.message) {
          return;
        }
        // Show success message from API
        const successMessage =
          typeof data === 'string'
            ? data
            : data?.message || t('managementSystem.toast.organizationDeleted');
        toast({
          title: t('managementSystem.toast.success'),
          description: successMessage,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      },
      onError: (error: any) => {
        console.error('Error deleting organization:', error);
      },
    });
  };

  const handleEditCompanyLimitations = (limitations: Partial<OrganizationLimitation>) => {
    if (!companyToEdit) return;

    updateLimitationsMutation.mutate(
      {
        id: companyToEdit.id,
        limitations,
      },
      {
        onSuccess: (data: any) => {
          if (!data?.success && data?.message) {
            return;
          }
          // Show success message from API
          const successMessage =
            typeof data === 'string'
              ? data
              : data?.message || t('managementSystem.toast.organizationUpdated');
          toast({
            title: t('managementSystem.toast.success'),
            description: successMessage,
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        },
        onError: (error: any) => {
          console.error('Error updating organization limitations:', error);
        },
      }
    );
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const columns = [
    {
      title: t('managementSystem.companyName'),
      width: { base: '150px', md: '240px' },
      dataIndex: 'name',
      key: 'name',
      notShowTooltip: true,
      render: (row: Organization) =>
        (row as any).isBlank ? (
          <Text color={colorMainText} fontSize={{ base: 'xs', md: 'sm' }} fontWeight='600' />
        ) : (
          <Flex align='center' gap={3}>
            <Flex
              align='center'
              justify='center'
              flexShrink={0}
              w='32px'
              h='32px'
              color='primary.solid'
              bg='primary.soft'
              borderRadius='md'
            >
              <Icon as={Building2} boxSize='16px' />
            </Flex>
            <Text color='text.default' fontSize={{ base: 'xs', md: 'sm' }} fontWeight='600'>
              {row.name}
            </Text>
          </Flex>
        ),
    },
    {
      title: t('managementSystem.noAccount'),
      width: { base: '70px', md: '90px' },
      dataIndex: 'user_count',
      key: 'user_count',
      render: (row: Organization) => (
        <Text color={colorSecondaryText} fontSize={{ base: 'xs', md: 'sm' }}>
          {(row as any).isBlank ? '' : row.user_count}
        </Text>
      ),
    },
    {
      title: t('managementSystem.storageUsageLimit'),
      width: { base: '80px', md: '120px' },
      dataIndex: 'storage_gb',
      key: 'storage_gb',
      render: (row: Organization) => (
        <>
          {(row as any).isBlank ? (
            <Text color={colorSecondaryText} fontSize={{ base: 'xs', md: 'sm' }}></Text>
          ) : row.limitation.storage_gb === null || row.limitation.storage_gb === undefined ? (
            <UnlimitedTag />
          ) : (
            <QuotaCell
              used={row.current_usage.total_storage_gb}
              limit={row.limitation.storage_gb || 0}
            />
          )}
        </>
      ),
    },
    {
      title: t('managementSystem.questionUsageLimit'),
      width: { base: '80px', md: '120px' },
      dataIndex: 'question',
      key: 'question',
      render: (row: Organization) => (
        <>
          {(row as any).isBlank ? (
            <Text color={colorSecondaryText} fontSize={{ base: 'xs', md: 'sm' }}></Text>
          ) : row.limitation.question === null || row.limitation.question === undefined ? (
            <UnlimitedTag />
          ) : (
            <QuotaCell used={row.current_usage.total_questions} limit={row.limitation.question} />
          )}
        </>
      ),
    },
    {
      title: t('managementSystem.totalTokenUsage'),
      width: { base: '120px', md: '130px' },
      dataIndex: 'token_usage',
      key: 'token_usage',
      render: (row: Organization) => (
        <>
          {(row as any).isBlank ? (
            <Text color={colorSecondaryText} fontSize={{ base: 'xs', md: 'sm' }}></Text>
          ) : (
            <Text color='text.muted' fontFamily='mono' fontSize={{ base: 'xs', md: 'sm' }}>
              {formatNumber(row.current_usage.total_input_token || 0)} /{' '}
              {formatNumber(row.current_usage.total_output_token || 0)}
            </Text>
          )}
        </>
      ),
    },
    {
      title: t('managementSystem.expirationDate'),
      width: { base: '80px', md: '120px' },
      dataIndex: 'expiration',
      key: 'expiration',
      isShowTooltip: false,
      render: (row: Organization) => (
        <>
          {(row as any).isBlank ? (
            <Text color={colorSecondaryText} fontSize={{ base: 'xs', md: 'sm' }}></Text>
          ) : row.limitation.expiration === 'Unlimited' ||
            row.limitation.expiration === null ||
            row.limitation.expiration === undefined ? (
            <Badge variant='ds-success'>{t('managementSystem.unlimited')}</Badge>
          ) : (
            <Badge variant='ds-neutral'>
              {new Date(row.limitation.expiration).toISOString().split('T')[0]}
            </Badge>
          )}
        </>
      ),
    },
    {
      key: 'actions',
      title: t('managementSystem.actions'),
      width: { base: '96px', md: '120px' },
      notShowTooltip: true,
      render: (row: Organization) => {
        if ((row as any).isBlank) {
          return <Box w='100%' h='32px' />;
        }
        // Colored action icons (matches the Documents table): edit/view = primary, delete = error.
        const actions = [
          {
            key: 'edit',
            label: t('managementSystem.tooltipEditCompany'),
            ariaLabel: 'Edit company',
            icon: Pencil,
            // Edit is management-only (no equivalent in the Documents table) → amber, distinct.
            color: 'state.warning',
            hover: { bg: 'state.warningSoft', color: 'state.warning' },
            onClick: () => handleEditCompany(row),
          },
          {
            key: 'view',
            label: t('managementSystem.viewUsage'),
            ariaLabel: 'View company usage',
            icon: Eye,
            // Same as the Documents table view action (consistency).
            color: 'primary.solid',
            hover: { bg: 'primary.soft', color: 'primary.solid' },
            onClick: () => handleViewUsage(row),
          },
          {
            key: 'delete',
            label: t('managementSystem.tooltipDeleteCompany'),
            ariaLabel: 'Delete company',
            icon: Trash2,
            color: 'state.error',
            hover: { bg: 'state.errorSoft', color: 'state.error' },
            onClick: () => handleDeleteCompany(row),
          },
        ];
        return (
          <Flex gap={{ base: 0.5, md: 1 }}>
            {actions.map((a) => {
              const btn = (
                <IconButton
                  color={a.color}
                  _hover={a.hover}
                  aria-label={a.ariaLabel}
                  icon={<Icon as={a.icon} boxSize='16px' />}
                  onClick={a.onClick}
                  size='sm'
                  variant='ghost'
                />
              );
              // Skip the tooltip while a modal is open (avoids stuck tooltips).
              return isAnyModalOpen ? (
                <Box key={a.key}>{btn}</Box>
              ) : (
                <Tooltip key={a.key} label={a.label}>
                  {btn}
                </Tooltip>
              );
            })}
          </Flex>
        );
      },
    },
  ];

  return (
    <PageContainer>
      {/* Page header — title left, Add company action right (mockup .page-header) */}
      <PageHeader
        title={t('managementSystem.title')}
        subtitle={t('managementSystem.subtitle', { count: companyData?.total_item || 0 })}
        action={
          <Button
            leftIcon={<Icon as={Plus} boxSize='16px' />}
            onClick={handleAddCompany}
            size='ds-md'
            variant='ds-primary'
          >
            {t('managementSystem.addNewCompany')}
          </Button>
        }
      />

      {/* Search toolbar — full width, below header (mockup .toolbar) */}
      <Flex align='center' gap={4} mb='5'>
        <Flex gap={3} w={{ base: '100%', md: 'auto' }}>
          <InputGroup w={{ base: '100%', md: '320px' }}>
            <InputLeftElement h='40px' color='text.subtle' pointerEvents='none'>
              <Icon as={Search} boxSize='16px' />
            </InputLeftElement>
            <Input
              h='40px'
              pl='38px'
              color='text.default'
              bg='bg.surface'
              borderColor='border.strong'
              borderRadius='md'
              _focus={{
                borderColor: 'primary.solid',
                boxShadow: '0 0 0 3px var(--chakra-colors-primary-soft)',
              }}
              _placeholder={{ color: 'text.subtle' }}
              onChange={handleSearchInputChange}
              onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) =>
                e.key === 'Enter' && handleSearch()
              }
              placeholder={t('managementSystem.searchPlaceholder')}
              value={searchTerm}
            />
          </InputGroup>
        </Flex>
      </Flex>

      {/* Company Table — flat bordered card (matches the Documents table; no shadow) */}
      <Box
        overflow='hidden'
        bg='bg.surface'
        borderWidth='1px'
        borderColor='border.default'
        borderRadius='lg'
      >
        <Box overflowX={{ base: 'auto', md: 'hidden' }}>
          <AmatriumTableV2
            columns={columns}
            data={companyData?.data || []}
            isLoading={isFetching}
            rowKey='id'
          />
        </Box>
        <Paginate
          total={companyData?.total_item || 0}
          current={currentPage + 1}
          pageSize={pageSize}
          onChange={(page: number) => handlePageChange(page - 1)}
          disabled={isFetching}
        />
      </Box>

      {/* Add Company Modal */}
      <AddCompanyModal
        isOpen={isAddCompanyModalOpen}
        onClose={onCloseAddCompanyModal}
        onSubmit={handleSubmitCompany}
      />

      {/* Delete Company Modal */}
      <DeleteCompanyModal
        isOpen={isDeleteCompanyModalOpen}
        onClose={() => {
          onCloseDeleteCompanyModal();
          setCompanyToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        organization={companyToDelete}
      />

      <EditCompanyModal
        isOpen={isEditCompanyModalOpen}
        onClose={() => {
          onCloseEditCompanyModal();
          setCompanyToEdit(null);
        }}
        organization={companyToEdit}
        onSubmit={handleEditCompanyLimitations}
      />
    </PageContainer>
  );
};

export default ManagementSystem;
